import React from 'react';
import { isNil } from 'lodash';
import * as ReactDOM from 'react-dom';
import EmbedContainer from 'react-oembed-component';
import {
  doExternalFetch,
  fetchOembed,
  getWindow,
  trackClickedSubscribeCTA,
  trackEvent,
} from '@surfline/web-common';

import { fetchReport } from 'common/api/spot';
import { fetchCamEmbedDetails } from 'common/api/cameras';
import { doJSFetch } from 'common/baseFetch';
import { SL_WEB_LINEUP_CAM_PAYWALL } from 'common/treatments';
import { getTreatmentFromWindow } from './treatments';
import AddToCalendar from 'components/AddToCalendar';
import CamPlayer from 'components/CamPlayer';
import GoogleDFP from 'components/GoogleDFP';
import PremiumCamCTA from 'components/PremiumCamCTA';
import PremiumVideoCTA from 'components/PremiumVideoCTA';
import config from 'config';
import loadAdConfig from 'utils/adConfig';
import { addToBlogheradsQueue, getBlogherads } from 'utils/blogherads';

const win = getWindow();

const swellEventArticleButton =
  "<div class='sl-swell-event__article__image__button__wrapper'><div class='sl-swell-event__article__image__button'><svg xmlns='http://www.w3.org/2000/svg' class='jw-svg-icon jw-svg-icon-fullscreen-on' viewBox='0 0 240 240' focusable='false'><path d='M96.3,186.1c1.9,1.9,1.3,4-1.4,4.4l-50.6,8.4c-1.8,0.5-3.7-0.6-4.2-2.4c-0.2-0.6-0.2-1.2,0-1.7l8.4-50.6c0.4-2.7,2.4-3.4,4.4-1.4l14.5,14.5l28.2-28.2l14.3,14.3l-28.2,28.2L96.3,186.1z M195.8,39.1l-50.6,8.4c-2.7,0.4-3.4,2.4-1.4,4.4l14.5,14.5l-28.2,28.2l14.3,14.3l28.2-28.2l14.5,14.5c1.9,1.9,4,1.3,4.4-1.4l8.4-50.6c0.5-1.8-0.6-3.6-2.4-4.2C197,39,196.4,39,195.8,39.1L195.8,39.1z'></path></svg></div></div>";

// Helper function to create a root and render
const renderWithRoot = (element, component) => {
  const root = ReactDOM.createRoot(element);
  root.render(component);
  return root;
};

export const getSkinPlayer = async (jwVideo, jwPlayer) => {
  if (!isNil(jwVideo) && !isNil(jwPlayer)) {
    try {
      const embedURL = `https://cdn.jwplayer.com/players/${jwVideo}-${jwPlayer}.js`;
      const data = await doJSFetch(embedURL);
      const result = data.substring(
        data.indexOf('var jwConfig = {'),
        data.indexOf('; // end config'),
      );
      const playerConfig = JSON.parse(result.replace('var jwConfig = ', ''));
      return playerConfig || {};
    } catch (_error) {
      return {};
    }
  }
  return {};
};

export const renderIgElements = () => {
  const igElements = getWindow()?.document.querySelectorAll('[data-sl-ig]');
  if (igElements.length) {
    [...igElements].map(async (ig) => {
      const {
        oembedObj: { html },
      } = await fetchOembed(ig.dataset.slIg, '/api/content/oembed');
      renderWithRoot(
        ig,
        <EmbedContainer markup={html}>
          {}
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </EmbedContainer>,
      );
    });
  }
};

export const renderJWElements = () => {
  let hasJwVideoStarted = false;
  const jwElements = getWindow()?.document.querySelectorAll("[id*='jw']");
  if (jwElements.length) {
    [...jwElements].map(async (jw) => {
      const [, jwId, jwPlayer] = jw.id.split('_');
      const playerConfig = await getSkinPlayer(jwId, jwPlayer);
      if (getWindow()?.jwplayer && jwId) {
        addToBlogheradsQueue(() => {
          const blogherads = getBlogherads();
          blogherads.setConf('vertical', 'PreRoll_Article');
        });
        let manuallyStartVideoTimeout;
        let player = getWindow()
          .jwplayer(jw.id)
          .setup({ ...playerConfig });
        const manuallyStartVideo = () => {
          if (
            hasJwVideoStarted === false &&
            playerConfig.advertising &&
            player.getState() !== 'playing'
          ) {
            delete playerConfig.advertising;
            player = getWindow()
              .jwplayer(jw.id)
              .setup({ ...playerConfig });
            player.play();
            hasJwVideoStarted = true;
          }
        };
        player
          .on('beforePlay', () => {
            manuallyStartVideoTimeout = setTimeout(manuallyStartVideo, 3000);
          })
          .on('adBreakStart', () => {
            hasJwVideoStarted = true;
          })
          .on('firstFrame', () => {
            hasJwVideoStarted = true;
          })
          .on('remove', () => {
            clearTimeout(manuallyStartVideoTimeout);
          });
      }
    });
  }
};

export const unmountJWElements = () => {
  const win = getWindow();
  if (!win?.document) return;

  const jwElements = win.document.querySelectorAll('.sl-jw-video-wrap');
  if (!jwElements.length) return;

  const jwplayer = win.jwplayer;
  if (typeof jwplayer !== 'function') return;

  jwElements.forEach((_player, index) => {
    try {
      const playerInstance = jwplayer(index);
      if (playerInstance && typeof playerInstance.remove === 'function') {
        playerInstance.remove();
      }
    } catch (_) {
      // Player may already be destroyed or unavailable during unmount
    }
  });
};

export const renderTwitterElements = () => {
  const twitterElements = win?.document.getElementsByClassName('twitter-tweet');
  if (twitterElements.length) {
    [...twitterElements].map(async (tweetElement) => {
      const tweetUrl = tweetElement.getElementsByTagName('a')[1].href;
      const {
        oembedObj: { html },
      } = await fetchOembed(tweetUrl, '/api/content/oembed');
      renderWithRoot(
        tweetElement,
        <EmbedContainer markup={html}>
          {}
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </EmbedContainer>,
      );
    });
  }
};

const createCameraObject = (cameraDetails, cameraId) => ({
  _id: cameraId,
  streamUrl: cameraDetails.cam.streamUrl,
  stillUrl: cameraDetails.cam.stillUrl,
  status: {
    isDown: !!cameraDetails.cam.isDown.status,
    message: cameraDetails.cam.isDown.message,
    subMessage: cameraDetails.cam.isDown.subMessage,
  },
  isPrerecorded: false,
  isPremium: cameraDetails.cam.isPremium,
  title: 'title',
});

/**
 * @param {boolean} isPermitted
 */
export const renderCameraEmbeds = (isPermitted) => {
  const cameraEmbeds = win?.document.querySelectorAll('[data-sl-cam-id]');
  if (cameraEmbeds?.length) {
    [...cameraEmbeds].map(async (embedElement) => {
      const cameraId = embedElement.getAttribute('data-sl-cam-id');
      const detailsJson = await fetchCamEmbedDetails(cameraId);
      const spotReport = await fetchReport({
        spotId: detailsJson.spot.id,
        includeLineupCams: getTreatmentFromWindow(SL_WEB_LINEUP_CAM_PAYWALL) !== 'off',
      });
      const camera = createCameraObject(detailsJson, cameraId);
      const camStillUrl = camera.stillUrl;
      const isPremiumCam = camera.isPremium;
      const showPremiumCamPaywall = isPremiumCam && !isPermitted;
      const segmentProperties = {
        location: 'premium only cam - cam embed',
        title: 'Premium Analysis',
        category: 'forecast',
        spotId: detailsJson.spot.id,
        spotName: detailsJson.spot.name,
      };
      if (showPremiumCamPaywall) {
        renderWithRoot(
          embedElement,
          <div className="video-wrap-container">
            <div className="video-wrap">
              <PremiumCamCTA
                camStillUrl={camStillUrl}
                href={`${config.surflineHost}${config.funnelUrl}`}
                onClickPaywall={() => trackClickedSubscribeCTA(segmentProperties)}
              />
            </div>
          </div>,
        );
      } else {
        renderWithRoot(
          embedElement,
          <div className="video-wrap-container">
            <div className="video-wrap">
              {/* TODO: Replace with CamPlayerV2 with final tear down */}
              <CamPlayer
                aspectRatio="16:9"
                adTag={{ iu: '/1024858/SL_Editorial_Embed_PreRoll' }}
                advertisingIds={spotReport.associated.advertising}
                playerId={cameraId || 'sl-editorial-embed'}
                camera={camera}
                spotName={detailsJson.spot.name}
                spotId={detailsJson.spot.id}
                isPermitted={isPermitted}
                isMultiCam={false}
                location="Editorial Embed"
              />
            </div>
          </div>,
        );
      }
    });
  }
};

export const renderYoutubeEmbeds = () => {
  const youtubeEmbedContainers = win?.document?.querySelectorAll(
    '.sl-swell-event__article-container .quiver-forecast-article-card__body > p',
  );

  if (youtubeEmbedContainers.length) {
    youtubeEmbedContainers.forEach((container) => {
      const youtubeIframe = container.querySelector('iframe[src^="https://www.youtube.com/embed/');

      if (youtubeIframe) {
        renderWithRoot(
          container,
          <div itemScope itemType="https://schema.org/VideoObject">
            <div style={{ position: 'relative', overflow: 'hidden', paddingBottom: '56.25%' }}>
              <iframe
                src={youtubeIframe.src}
                width="100%"
                height="100%"
                title="Youtube Video"
                style={{ position: 'absolute' }}
                allowFullScreen
              />
            </div>
          </div>,
        );
      }
    });
  }
};

export const replaceWithClickableImage = (onClick, getImages) => {
  const imgElements = win?.document.querySelectorAll(
    '.sl-swell-event__content__wrapper p > img, .wp-caption > img',
  );
  const images = [];
  if (imgElements.length) {
    [...imgElements].map((img, index) => {
      img.onclick = () => onClick(index);
      img.insertAdjacentHTML('afterend', swellEventArticleButton);
      images.push({ src: img.src, w: img.clientWidth, h: img.clientHeight, index });
      return img;
    });
  }
  getImages(images);
  return null;
};

export const renderFeaturedVideo = (jwId) => {
  const featuredVideoElements = win?.document.getElementsByClassName(
    'sl-swell-event-featured-media',
  );
  if (featuredVideoElements.length) {
    [...featuredVideoElements].map(async (jw) => {
      if (win && win.jwplayer && jwId) {
        win
          .jwplayer(jw.id)
          .setup({ playlist: `https://content.jwplatform.com/feeds/${jwId}.json`, ph: 2 });
      }
    });
  }
};

export const renderResizedImages = () => {
  const imgElements = win?.document.getElementsByTagName('img');
  if (imgElements.length) {
    [...imgElements].map((img) => {
      if (img.src.indexOf('/wp-content/uploads') > -1) {
        img.src = config.cloudflareImageResizingUrl(img.src);
        return img;
      }
      return img;
    });
  }
};

const renderAd = (element, adNumber, adIdentifier, dfpKeyword, qaFlag, entitlements, isUser) => {
  element.insertAdjacentHTML('afterend', `<div id="ad-${adNumber}" />`);
  const adElement = getWindow()?.document.getElementById(`ad-${adNumber}`);
  if (adElement) {
    renderWithRoot(
      adElement,
      <GoogleDFP
        adConfig={loadAdConfig(
          adIdentifier,
          [
            ['qaFlag', qaFlag],
            ['contentKeyword', dfpKeyword],
          ],
          entitlements,
          isUser,
        )}
      />,
    );
  }
};

export const renderSurfNetworkVideo = async () => {
  const surfNetworkContainerElement = getWindow()?.document.getElementById('sl-surfnetwork-video');
  if (surfNetworkContainerElement) {
    const videoId = surfNetworkContainerElement.getAttribute('data-sl-video-id');
    if (videoId) {
      const surfNetworkToken = await doExternalFetch('/api/content/surfnetwork');
      renderWithRoot(
        surfNetworkContainerElement,
        <div itemScope itemType="https://schema.org/VideoObject">
          <div style={{ position: 'relative', overflow: 'hidden', paddingBottom: '56.25%' }}>
            <iframe
              src={`https://api.nodplatform.com/partner/surfline/watch/${videoId}.html?access_token=${surfNetworkToken}`}
              width="100%"
              height="100%"
              title="SurfNetwork"
              style={{ position: 'absolute' }}
              allowFullScreen
            />
          </div>
        </div>,
      );
    }
  }
};

export const renderArticleAds = (
  articleRef,
  children,
  dfpKeyword,
  createdAt,
  qaFlag,
  entitlements,
  isUser,
) => {
  const adTracker = { count: 0, placed: 0 };
  children.forEach((node, index) => {
    if (
      node.tagName === 'P' ||
      node.tagName === 'IMG' ||
      node.className.indexOf('video') > -1 ||
      node.className.indexOf('img') > -1
    ) {
      adTracker.count += 1;
    }
    if (adTracker.count === 4 && adTracker.placed < 3) {
      const adNumber = adTracker.placed + 1;
      const adIdentifier =
        adNumber === 1 && new Date(createdAt) < new Date('2019-04-29 00:00:00')
          ? 'legacyEditContent'
          : `editBody${adNumber}`;
      renderAd(
        articleRef.current.children[index],
        adNumber,
        adIdentifier,
        dfpKeyword,
        qaFlag,
        entitlements,
        isUser,
      );
      adTracker.count = -1;
      adTracker.placed += 1;
    } else if (index === children.length - 1 && adTracker.placed === 0) {
      const adNumber = adTracker.placed + 1;
      renderAd(
        articleRef.current.children[index],
        adNumber,
        `editBody${adNumber}`,
        dfpKeyword,
        qaFlag,
        entitlements,
        isUser,
      );
    }
  });
};

export const trackArticleLinkClicks = (articleId) => {
  const articleBody = getWindow()?.document.getElementById('sl-editorial-article-body');
  const articleLinks = articleBody.querySelectorAll('a');
  if (articleLinks.length) {
    [...articleLinks].map(async (ig, index) => {
      articleLinks[index].addEventListener(
        'click',
        (event) =>
          trackEvent('Clicked Link', {
            articleId,
            category: 'editorial',
            linkLocation: 'editorialContent',
            linkName: event.target.innerText || articleLinks[index].innerText,
            linkUrl: event.target.href || articleLinks[index].href,
          }),
        false,
      );
    });
  }
};

export const renderVideoPaywalls = (onClickVideoPaywall, onClickSignIn, shouldPaywall) => {
  const videoPaywallElement = getWindow()?.document.getElementById('sl-premium-video-cta');
  const videoPaywallCaptionElements =
    getWindow()?.document.getElementsByClassName('sl-premium-video-text');

  if (videoPaywallElement && shouldPaywall) {
    renderWithRoot(
      videoPaywallElement,
      <PremiumVideoCTA
        onClickVideoPaywall={onClickVideoPaywall}
        imageUrl={videoPaywallElement.getAttribute('data-image-url')}
        onClickSignIn={onClickSignIn}
        videoText={
          videoPaywallCaptionElements.length ? videoPaywallCaptionElements[0].innerHTML : ''
        }
      />,
    );
  }
};

const onClickAddToCalendar = (trackData) => {
  trackEvent('Clicked Link', trackData);
};

export const renderCalendarEvents = (articleId) => {
  const eventElements = getWindow()?.document.getElementsByClassName('cal-event');
  if (eventElements.length) {
    [...eventElements].map(async (event) => {
      try {
        const spotReport = await fetchReport({
          spotId: event.querySelector('#cal-event-spot').innerHTML,
          includeLineupCams: getTreatmentFromWindow(SL_WEB_LINEUP_CAM_PAYWALL) !== 'off',
        });
        const {
          spot: { name: spotName },
          associated: { href: spotUrl },
        } = spotReport;
        const startTime = event.querySelector('#cal-event-start').innerHTML.toString();
        const endTime = event.querySelector('#cal-event-end').innerHTML.toString();
        const eventObj = {
          title: `Surf ${spotName}`,
          description: `Check out the forecast for ${spotName} at ${spotUrl}`,
          location: spotName,
          startTime,
          endTime,
        };
        const trackData = {
          articleId,
          category: 'editorial',
          linkLocation: 'editorialContent',
          linkName: 'Add to Calendar',
          linkUrl: spotUrl,
        };
        renderWithRoot(
          event,
          <AddToCalendar onClickAdd={() => onClickAddToCalendar(trackData)} event={eventObj} />,
        );
      } catch (err) {
        console.error(err);
      }
    });
  }
};

export const renderCalendarCinemaEvents = (articleId) => {
  const eventElements = getWindow()?.document.getElementsByClassName('cal-event-cinema');
  if (eventElements.length) {
    [...eventElements].map(async (event) => {
      try {
        const startTime = event.querySelector('#cal-event-start').innerHTML.toString();
        const endTime = event.querySelector('#cal-event-end').innerHTML.toString();
        const description = event.querySelector('#cal-event-description').innerHTML.toString();
        const title = event.querySelector('#cal-event-title').innerHTML.toString();
        const link = event.querySelector('#cal-event-link').innerHTML.toString();
        const eventObj = {
          title,
          description: `${description}
          ${link}`,
          startTime,
          endTime,
        };
        const trackData = {
          articleId,
          category: 'editorial',
          linkLocation: 'editorialContent',
          linkName: 'Add to Calendar',
          linkUrl: link,
        };
        renderWithRoot(
          event,
          <AddToCalendar onClickAdd={() => onClickAddToCalendar(trackData)} event={eventObj} />,
        );
      } catch (err) {
        console.error(err);
      }
    });
  }
};
